/**
 * Utility functions for adding timestamp and location overlay to images
 */

/**
 * Get current location coordinates (detailed)
 * @returns {Promise<{latitude: number, longitude: number}>}
 */
export const getCurrentLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation is not supported"));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      (error) => {
        reject(error);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  });
};

/**
 * Get formatted timestamp
 * @returns {string}
 */
export const getFormattedTimestamp = () => {
  return new Date().toLocaleString("en-IN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
};

const formatOverlayDateTime = (d = new Date()) => {
  const date = d.toLocaleDateString("en-IN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const time = d.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  return { date, time };
};

const buildCustomerLabel = (meta) => {
  if (!meta) return null;
  const name = meta.customerName?.trim();
  const village = meta.village?.trim();
  const region = meta.region?.trim();

  const parts = [];
  if (name) parts.push(name);
  if (village) parts.push(village);
  if (region) parts.push(region);

  if (parts.length === 0) return null;
  return `Customer: ${parts.join(" | ")}`;
};

/**
 * Overlay text on an image
 * @param {File} file - The image file to process
 * @param {Object} overlay - overlay info
 * @returns {Promise<File>} - Modified image file with overlay
 */
export const overlayTextOnImage = (file, overlay) => {
  return new Promise((resolve, reject) => {
    const img = new window.Image(); // Use native Image constructor
    const reader = new FileReader();

    reader.onload = (e) => {
      img.onload = () => {
        // Create canvas
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");

        // Draw the original image
        ctx.drawImage(img, 0, 0);

        const fontSize = Math.max(
          16,
          Math.min(img.width, img.height) * 0.028
        );
        const lineHeight = fontSize * 1.25;
        const padding = fontSize * 0.6;

        ctx.font = `bold ${fontSize}px Arial, sans-serif`;

        const lines = [];
        if (overlay?.customerLine) lines.push(overlay.customerLine);
        if (overlay?.date && overlay?.time)
          lines.push(`Date: ${overlay.date} | Time: ${overlay.time}`);

        if (typeof overlay?.latitude === "number" && typeof overlay?.longitude === "number") {
          lines.push(
            `Lat: ${overlay.latitude.toFixed(6)} | Lng: ${overlay.longitude.toFixed(6)}`
          );
        } else {
          lines.push("Lat: N/A | Lng: N/A");
        }

        // Measure max width
        const maxWidth = Math.max(...lines.map((l) => ctx.measureText(l).width));
        const boxWidth = maxWidth + padding * 2;
        const boxHeight = lines.length * lineHeight + padding * 1.5;

        const x = padding;
        const y = canvas.height - boxHeight - padding;

        // Background box
        ctx.fillStyle = "rgba(0,0,0,0.65)";
        ctx.fillRect(x, y, boxWidth, boxHeight);

        // Text
        ctx.fillStyle = "white";
        ctx.strokeStyle = "rgba(0,0,0,0.8)";
        ctx.lineWidth = Math.max(2, fontSize * 0.12);

        lines.forEach((line, i) => {
          const textX = x + padding;
          const textY = y + padding + (i + 1) * lineHeight - lineHeight * 0.25;
          ctx.strokeText(line, textX, textY);
          ctx.fillText(line, textX, textY);
        });

        // Convert canvas to blob (compress for smaller uploads, especially on mobile)
        const maxDim = 1920;
        const quality = 0.85;
        let w = canvas.width;
        let h = canvas.height;
        let outputCanvas = canvas;
        if (w > maxDim || h > maxDim) {
          const scale = maxDim / Math.max(w, h);
          w = Math.round(w * scale);
          h = Math.round(h * scale);
          const resized = document.createElement("canvas");
          resized.width = w;
          resized.height = h;
          const rctx = resized.getContext("2d");
          if (rctx) {
            rctx.drawImage(canvas, 0, 0, w, h);
            outputCanvas = resized;
          }
        }
        outputCanvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error("Failed to create image blob"));
              return;
            }
            const modifiedFile = new File([blob], file.name.replace(/\.[^.]+$/, ".jpg"), {
              type: "image/jpeg",
              lastModified: Date.now(),
            });
            resolve(modifiedFile);
          },
          "image/jpeg",
          quality
        );
      };

      img.onerror = () => {
        reject(new Error("Failed to load image"));
      };

      img.src = e.target.result;
    };

    reader.onerror = () => {
      reject(new Error("Failed to read file"));
    };

    reader.readAsDataURL(file);
  });
};

/**
 * Process multiple image files with timestamp and location overlay
 * @param {File[]} files - Array of image files to process
 * @param {Object} overlayMeta - { customerName, village, region }
 * @param {Object|null} capturedLocation - Optional pre-captured coords (e.g. from survey Capture). If present with latitude/longitude, used instead of current GPS so uploads from a different location still show site coords.
 * @returns {Promise<File[]>} - Array of processed image files
 */
export const processImagesWithOverlay = async (files, overlayMeta = null, capturedLocation = null) => {
  if (files.length === 0) return [];

  try {
    // Use pre-captured location when available (e.g. user uploaded images later from different place)
    const hasCapturedCoords =
      capturedLocation &&
      typeof capturedLocation.latitude === "number" &&
      typeof capturedLocation.longitude === "number";

    let location = null;
    if (hasCapturedCoords) {
      location = {
        latitude: capturedLocation.latitude,
        longitude: capturedLocation.longitude,
      };
    } else {
      try {
        location = await getCurrentLocation();
      } catch (error) {
        console.warn("Failed to get location, proceeding without coordinates:", error);
      }
    }

    const now = new Date();
    const { date, time } = formatOverlayDateTime(now);
    const customerLine = buildCustomerLabel(overlayMeta);
    const overlay = {
      date,
      time,
      customerLine,
      latitude: location?.latitude,
      longitude: location?.longitude,
    };

    // Process each image with overlay
    const processedFiles = await Promise.all(
      files.map(async (file) => {
        return await overlayTextOnImage(file, overlay);
      })
    );

    return processedFiles;
  } catch (error) {
    console.error("Error processing images:", error);
    // Fallback: return original files if processing fails
    return files;
  }
};
